import { type IFrameOptions } from './option'
import { type ClientProviderPluginInstallOption, type IClientProviderPluginInstaller } from '../models/plugin-type'
import IframeProtocol from '@source-digital/iframe-protocol'
import { IFrameMessengerAdapter } from './messenger'
import { type IClientProviderPluginContext, type IClientProviderPlugin } from '@source-digital/sd-app-core'
import { type IBrokerContext } from '../../context'
import { BaseClientProviderPluginInstaller } from '../base'
import type IframeProtocolTarget from '@source-digital/iframe-protocol/dist/types/iframe-protocol-target'
import { nanoid } from 'nanoid'

export interface IFramePluginInstallOption extends ClientProviderPluginInstallOption<'iframe'>, Omit<IFrameOptions, 'url'> {
  url?: string
  el?: HTMLIFrameElement
  style?: Record<string, any>
  renderless?: boolean
}

export class IFrameClientProviderPlugin implements IClientProviderPlugin {
  ip: IframeProtocol
  config: IFramePluginInstallOption
  iframeEl: HTMLIFrameElement | null = null
  ipTarget: IframeProtocolTarget | null = null
  #resizeObserver: ResizeObserver | null = null
  constructor (config: IFramePluginInstallOption, ip: IframeProtocol) {
    this.config = config
    this.ip = ip
  }

  async #attach () {
    if (this.config.el != null) {
      this.ipTarget = await this.ip.attach(this.config.el)
      this.iframeEl = this.config.el
      return
    }
    if (this.config.url == null) throw new Error('`el` or `url` must be specified.')
    // create iframe
    const iframe = window.document.createElement('iframe')
    iframe.setAttribute('src', this.config.url)
    // Mandatory unsecure sandbox attributes are a known limitation of iframe messaging.
    // Related: https://github.com/Source-Digital/iframe-protocol/issues/65
    iframe.setAttribute('sandbox', `allow-scripts allow-same-origin ${this.config.sandbox ?? ''}`)
    if (this.config.hide === true || this.config.style == null) {
      iframe.style.display = 'none'
    } else if (this.config.style != null) {
      const style = Object.assign({}, { border: 'none' }, this.config.style)
      Object.entries(style).forEach(([key, value]) => {
        iframe.style.setProperty(key, value)
      })
    }

    // insert to DOM
    let targetEl: Element | null
    if (this.config.targetEl != null) {
      targetEl = typeof this.config.targetEl === 'string' ? window.document.querySelector(this.config.targetEl) : this.config.targetEl
      if (targetEl == null) throw new Error('A target element could not be found.' + (typeof this.config.targetEl === 'string' ? ` Selector: ${this.config.targetEl}` : ''))
    } else {
      targetEl = window.document.body
    }
    targetEl.appendChild(iframe)

    // We need to wait until the iframe is loaded.
    // If we try to reference iframe.contentWindow before it is loaded,
    // we get a security error. Ideally the iframe-protocol library would
    // handle this when attaching to an existing iframe. Note that it does
    // handle this when it creates its own iframe.
    await new Promise((resolve, reject) => {
      iframe.addEventListener('load', resolve)
      iframe.addEventListener('error', reject)
    })

    // TODO store targets with namespace etc
    this.ipTarget = await this.ip.attach(iframe)
    this.iframeEl = iframe
  }

  #observeResize (el: Element, onResize: (data: any) => void) {
    if (this.config.observeResize == null || this.config.observeResize === false) return
    // watch element resize
    let observeEl
    if (typeof this.config.observeResize === 'string') {
      observeEl = window.document.querySelector(this.config.observeResize)
      if (observeEl == null) console.warn('A target element to observe could not be found. Selector:' + this.config.observeResize)
    } else {
      observeEl = el
    }

    if (observeEl != null) {
      const ro = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect
        onResize({ width, height })
      })
      ro.observe(observeEl)
      this.#resizeObserver = ro
    }
  }

  async install (ctx: IClientProviderPluginContext) {
    await this.#attach()
    if (this.iframeEl != null) {
      this.#observeResize(this.iframeEl, (data) => {
        ctx.messenger.emit('resize', data)
      })
    }
  }

  async uninstall () {
    this.iframeEl?.remove()
    this.#resizeObserver?.disconnect()
  }
}

export class IFrameClientProviderPluginInstaller extends BaseClientProviderPluginInstaller implements IClientProviderPluginInstaller<'iframe', IFramePluginInstallOption> {
  ip: IframeProtocol
  messengerAdapter: IFrameMessengerAdapter
  constructor (ctx: IBrokerContext) {
    super(ctx, 'IFrameClientProviderPluginInstaller')
    this.ip = IframeProtocol.init({
      name: 'BrokerMessenger',
      allowUnsafeTarget: false,
      attachParent: false,
      requireReady: true,
      logger: console.debug // FIXME
    })
    this.messengerAdapter = new IFrameMessengerAdapter(this.ip)
    ctx.rootMessenger.addMessenger('iframe', this.messengerAdapter)
  }

  async install (installOption: IFramePluginInstallOption): Promise<{ id: string, name?: string, plugin: IClientProviderPlugin }> {
    const { name } = installOption
    const module = new IFrameClientProviderPlugin(installOption, this.ip)
    const id = `iframe-${nanoid()}`
    await module.install(this.createContext(module, id))
    if (module.ipTarget == null) throw new Error('Failed to attach iframe.')
    this.logger.log('Installed client plugin.', installOption)
    this.messengerAdapter.targetMap.set(id, module.ipTarget)
    return {
      id,
      name,
      plugin: module
    }
  }

  async uninstall (module: IClientProviderPlugin): Promise<void> {
    await module.uninstall()
  }
}
